/**
 * Per-product browser and app icons.
 *
 * One React bundle serves two products, and they must not wear each other's
 * icon:
 *
 *   "/" and the marketing pages  -> Aimepic  (teal field, aimepic-* assets)
 *   "/onecare" and below         -> OneCare  (blue badge, onecare-* assets)
 *
 * That covers the browser tab, the iOS home-screen icon, the theme colour AND
 * the web-app manifest.
 *
 * IT DOES NOT COVER INSTALLING THE APP, and cannot. Install metadata is read
 * from the HTML as the server delivered it: iOS Safari takes apple-touch-icon
 * from the parsed document and ignores anything JS changes afterwards, and
 * Chrome snapshots the manifest early in page load then freezes the icon at
 * install time. A JS swap is always too late. So "Add to Home Screen" from
 * inside OneCare used to pin the Aimepic icon.
 *
 * The fix lives in the build and the server: scripts/make-brand-shells.js emits
 * build/onecare.html with the OneCare tags already in it, and nginx serves that
 * for /onecare* (frontend/nginx.conf). This hook remains for what a static
 * shell cannot do — client-side navigation BETWEEN the two products, where no
 * new HTML is fetched. Keep the two in step: a change here needs the same
 * change in make-brand-shells.js.
 *
 * Why this rewrites tags instead of rendering new ones: index.html already
 * ships a full icon set (it has to — it is what the browser sees before any JS
 * runs). Adding a second <link rel="icon"> via Helmet would leave TWO icon
 * declarations in the head and let the browser choose; browsers do not
 * reliably prefer the later one, and Chrome ignores a second rel="manifest"
 * outright. Rewriting the href of the existing tags, matched by id, leaves
 * exactly one declaration of each at all times.
 *
 * The artwork is generated by docs/branding/onecare-icon/make_onecare_icons.py
 * and docs/branding/aimepic-icon/make_brand_icons.py — never hand-edited.
 */

import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Cache-buster for files under public/, whose names are stable across deploys.
 * Bump when the artwork changes, and keep index.html + both manifest files in
 * step or browsers keep serving the icon they already have.
 * v2 = Aimepic teal-glow set. v3 = OneCare badge set split out from it.
 */
export const ICON_VERSION = '3';

interface BrandIcons {
  /** Legacy multi-resolution icon; still what several browsers prefer. */
  faviconIco: string;
  /** 128px tab icon. */
  faviconPng: string;
  /** iOS home screen. Must be opaque — iOS composites alpha to black. */
  appleTouch: string;
  /** Web-app manifest driving the installed app's name, icon and start URL. */
  manifest: string;
  /** Browser UI tint, and the installed app's title bar. */
  themeColor: string;
}

const AIMEPIC: BrandIcons = {
  faviconIco: 'favicon.ico',
  faviconPng: 'aimepic-favicon.png',
  appleTouch: 'apple-touch-icon.png',
  manifest: 'manifest.json',
  themeColor: '#0d516e',
};

const ONECARE: BrandIcons = {
  faviconIco: 'favicon-onecare.ico',
  faviconPng: 'favicon-onecare.png',
  appleTouch: 'onecare-apple-touch.png',
  manifest: 'manifest-onecare.json',
  themeColor: '#12539f',
};

/** True for the OneCare product and everything inside it, login included. */
export const isOneCarePath = (pathname: string): boolean =>
  pathname === '/onecare' || pathname.startsWith('/onecare/');

/**
 * Find the tag by id, falling back to its `rel` — a browser holding a cached
 * index.html from before the ids existed still gets the right icons.
 */
const findTag = (id: string, selector: string): HTMLElement | null =>
  document.getElementById(id) ?? document.head.querySelector<HTMLElement>(selector);

const setHref = (id: string, selector: string, href: string): void => {
  const tag = findTag(id, selector) as HTMLLinkElement | null;
  if (tag && tag.getAttribute('href') !== href) tag.setAttribute('href', href);
};

export function useBrandIcons(): void {
  const { pathname } = useLocation();

  useEffect(() => {
    const brand = isOneCarePath(pathname) ? ONECARE : AIMEPIC;
    const base = process.env.PUBLIC_URL || '';
    const asset = (file: string) => `${base}/${file}?v=${ICON_VERSION}`;

    setHref('brand-favicon-ico', 'link[rel="icon"][sizes="any"]', asset(brand.faviconIco));
    setHref('brand-favicon-png', 'link[rel="icon"][type="image/png"]', asset(brand.faviconPng));
    setHref('brand-apple-touch', 'link[rel="apple-touch-icon"]', asset(brand.appleTouch));
    // No ?v= on the manifest: it is fetched fresh by the browser and a changing
    // URL would make Chrome treat it as a different app on every version bump.
    setHref('brand-manifest', 'link[rel="manifest"]', `${base}/${brand.manifest}`);

    const themeTag = findTag('brand-theme-color', 'meta[name="theme-color"]');
    if (themeTag && themeTag.getAttribute('content') !== brand.themeColor) {
      themeTag.setAttribute('content', brand.themeColor);
    }
  }, [pathname]);
}

export default useBrandIcons;
